import { useContext, useMemo } from 'react';
import { sortBy } from 'lodash-es';
import { MdAdd, MdChevronLeft, MdChevronRight, MdOutlineStickyNote2 } from 'react-icons/md';
import { Button, IconButton } from '@ds';
import { CaseNotesDrawer } from '@lp/features/modals/CaseNotesDrawer';
import { AuthContext } from '@lp/core/auth';
import TabSection from '@features/components/CaseOverview/components/TabSection/TabSection';
import { CaseNoteCard } from '@lp/features/CaseNoteCard';
import { useCaseNotesStore } from '@/store/case-notes.store';
import { CaseNote, CaseStatus } from '@/api/apis/case';
import { useRowScroll } from '@/hooks/useRowScroll';
import { CaseDetailsPermissions } from '@/permissions';
import { isAllowedStatus } from '@/utils/helpers';

type NotesSectionRowProps = {
  caseId: string;
  notes: CaseNote[];
  status: CaseStatus;
};

export const NotesSectionRow = ({ caseId, notes, status }: NotesSectionRowProps) => {
  const drawerOpened = useCaseNotesStore.use.drawerOpened();
  const setDrawerOpened = useCaseNotesStore.use.setDrawerOpened();
  const setCreateNote = useCaseNotesStore.use.setCreateNote();
  const { checkPermission } = useContext(AuthContext);
  const hasNoteUpdatePermission = checkPermission(CaseDetailsPermissions.NoteUpdate);
  const canAddNoteInCurrentStatus = isAllowedStatus({ currentStatus: status, statusTo: CaseStatus.ReturningToBase });
  const {
    listContainerRef,
    listItemRefs,
    onNavigationPrev,
    onNavigationNext,
    visibleFrom,
    visibleTo,
    maxLeft,
    maxRight,
  } = useRowScroll();

  const sortedNotes = useMemo(() => {
    return sortBy(notes, [(n) => n.isPinned, (n) => n.createdAt || n.modifiedAt]).reverse();
  }, [notes]);

  return (
    <>
      <TabSection
        title="Case Notes"
        actionRender={
          hasNoteUpdatePermission &&
          canAddNoteInCurrentStatus && (
            <Button size="sm" variant="outline" startContent={<MdAdd />} onClick={() => setCreateNote(true)}>
              Add Note
            </Button>
          )
        }
      >
        {notes.length ? (
          <>
            <div className="grid relative">
              <div className="flex gap-4 overflow-auto snap-x snap-mandatory" ref={listContainerRef}>
                {sortedNotes.map((note, i) => (
                  <div
                    key={note.noteId}
                    className="min-w-[278px] flex snap-start "
                    ref={(el) => {
                      listItemRefs.current[i] = el;
                    }}
                  >
                    <CaseNoteCard
                      caseId={caseId}
                      data={note}
                      readonly={!hasNoteUpdatePermission}
                      light
                      isScrollable
                      status={status}
                    />
                  </div>
                ))}
              </div>
            </div>
            <div className="flex w-full justify-between items-center max-h-6 mt-4">
              <Button size="sm" variant="light" onClick={() => setDrawerOpened(true)}>
                See All Notes
              </Button>
              <div className="flex items-center gap-2">
                <IconButton
                  size="xs"
                  variant="filled"
                  disableRipple
                  isDisabled={maxLeft}
                  startContent={<MdChevronLeft />}
                  onClick={onNavigationPrev}
                />
                <span className="text-s-r text-base-20">
                  {visibleFrom + 1}-{visibleTo + 1}/{notes.length}
                </span>
                <IconButton
                  size="xs"
                  variant="filled"
                  disableRipple
                  isDisabled={maxRight}
                  startContent={<MdChevronRight />}
                  onClick={onNavigationNext}
                />
              </div>
            </div>
          </>
        ) : (
          <div className="flex flex-col gap-3 items-center justify-center w-full min-h-[176px]">
            <MdOutlineStickyNote2 size={60} className="text-base-50" />
            <span className="text-l-r text-base-30">No case notes have been added</span>
          </div>
        )}
      </TabSection>

      <CaseNotesDrawer caseId={caseId} isOpen={drawerOpened} onClose={() => setDrawerOpened(false)} notes={notes} />
    </>
  );
};
